import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import { unitMeta, UNIT_OPTIONS } from './units';
import { ritmoRecente, velocidadePlausivel, decimal } from './ritmo';
import { BookCover, Flame, Sheet, TopBar } from './common';
import { BookV2, BookStatusDesign, AuthSession } from '../types';
import { BookReview, BookConversation } from './book-conversation';
import { api } from '../services/apiV2';
import { FocusTimer } from './focus-mode';
import { BadgeMentor, ConviteTrilha } from './mentor-livro/trilha';

// Lápis de edição (inline) — usado nos vários pontos editáveis do design.
const PencilBtn: React.FC<{ onClick?: () => void; size?: number; style?: React.CSSProperties }> = ({ onClick, size = 11, style }) => (
  <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', flex: 'none', color: 'var(--ink-3)', ...style }}>
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
  </button>
);

const STATUSES: { id: BookStatusDesign; label: string }[] = [
  { id: 'wishlist', label: 'Próximo' },
  { id: 'reading', label: 'Lendo' },
  { id: 'paused', label: 'Parado' },
  { id: 'finished', label: 'Lido' },
];

export const BookDetailScreen: React.FC<{
  book: BookV2;
  onBack: () => void;
  onLogRead: () => void;
  onOpenStudy?: () => void;
  onStatusChange: (id: string, newStatus: BookStatusDesign) => void;
  onDelete?: () => void;
  onUpdateGoal?: (bookId: string, pages: number) => Promise<void> | void;
  onSearchCover?: (bookId: string) => Promise<void> | void;
  onUploadCover?: (bookId: string, file: File) => Promise<void> | void;
  onEditTarget?: (bookId: string, date: string | null) => Promise<void> | void;
  onSyncPage?: (bookId: string, page: number) => Promise<void> | void;
  /** Edita titulo/autor/total de paginas — mesmos campos do AddBookSheet. */
  onEditDetails?: (bookId: string, patch: { title?: string; author?: string; totalPages?: number; progressUnit?: 'pages' | 'chapters' | 'positions'; currentPage?: number }) => Promise<void> | void;
  // ── Página do Livro como objeto social (resenha + conversa) ──
  session?: AuthSession | null;
  myName?: string;
  myAvatarUrl?: string | null;
  onOpenProfile?: (uid: string) => void;
  onBlockUser?: (uid: string, name: string) => Promise<void> | void;
  onSaveReview?: (bookId: string, rating: number | null, review: string) => Promise<void> | void;
  /** O livro (meu) foi apagado em outro aparelho: o App tira da lista e volta à estante (QA-30). */
  onLivroSumiu?: () => void;
  /** Notas do Mentor liberadas e ainda não abertas: o número vermelho do caderno. */
  mentorNovas?: number;
}> = ({ book, onBack, onLogRead, onOpenStudy, onStatusChange, onDelete, onUpdateGoal, onSearchCover, onUploadCover, onEditTarget, onSyncPage, onEditDetails, session, myName, myAvatarUrl, onOpenProfile, onBlockUser, onSaveReview, onLivroSumiu, mentorNovas = 0 }) => {
  const u = unitMeta(book.progressUnit);
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [coverSheetOpen, setCoverSheetOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState(String(book.dailyGoal || 20));
  const [goalSaving, setGoalSaving] = useState(false);
  const [coverBusy, setCoverBusy] = useState<string | null>(null);
  const [coverMsg, setCoverMsg] = useState<string | null>(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncInput, setSyncInput] = useState(String(book.read || 0));
  const [syncSaving, setSyncSaving] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const reviewRef = useRef<HTMLDivElement>(null);

  // Página do Livro como objeto social: busca dono/visitante + canComment.
  // Para o DONO o livro vem da prop (lista completa). Para VISITANTE (livro de
  // outra pessoa, aberto pela estante ou deep-link), a prop é mínima {id} e os
  // dados vêm daqui.
  const [pageData, setPageData] = useState<any>(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [pageStatus, setPageStatus] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    setPageLoaded(false); setPageData(null); setPageStatus(null);
    api.bookPage(session ?? null, book.id)
      .then(p => { if (alive) { setPageData(p); setPageLoaded(true); } })
      .catch((e: any) => {
        if (!alive) return;
        setPageStatus(e?.status || null);
        setPageLoaded(true);
        // Meu livro (veio da minha lista, com título) e o servidor diz que não
        // existe: foi apagado em outro aparelho (pente fino, QA-30).
        if (e?.status === 404 && book.title && onLivroSumiu) onLivroSumiu();
      });
    return () => { alive = false; };
  }, [book.id, session]);

  // Sheet de editar dados do livro (titulo/autor/paginas)
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState(book.title);
  const [editAuthor, setEditAuthor] = useState(book.author);
  const [editPages, setEditPages] = useState(String(book.pages || 0));
  const [editUnit, setEditUnit] = useState<'pages' | 'chapters' | 'positions'>((book.progressUnit as any) || 'pages');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const editUnitChanged = editUnit !== (book.progressUnit || 'pages');
  const eu = unitMeta(editUnit);

  useEffect(() => {
    if (editOpen) {
      setEditTitle(book.title);
      setEditAuthor(book.author);
      setEditPages(String(book.pages || 0));
      setEditUnit((book.progressUnit as any) || 'pages');
      setEditError(null);
    }
  }, [editOpen, book.title, book.author, book.pages, book.progressUnit]);

  const doSaveDetails = async () => {
    if (!onEditDetails) return;
    const title = editTitle.trim();
    const author = editAuthor.trim();
    const pages = parseInt(editPages, 10);
    if (!title) { setEditError('Título não pode ficar vazio.'); return; }
    if (!author) { setEditError('Autor não pode ficar vazio.'); return; }
    if (!Number.isFinite(pages) || pages < 1 || pages > 500000) {
      setEditError(`Número de ${eu.long} inválido (1 a 500.000).`);
      return;
    }
    // Se diminuir total abaixo da leitura atual, avisa — mas só quando a unidade
    // NÃO mudou (ao trocar unidade a posição volta pra 0, então não há conflito).
    if (!editUnitChanged && pages < book.read) {
      setEditError(`O total (${pages}) não pode ser menor que a sua posição atual (${book.read}).`);
      return;
    }
    setEditSaving(true); setEditError(null);
    try {
      const patch: any = {};
      if (title !== book.title) patch.title = title;
      if (author !== book.author) patch.author = author;
      if (pages !== book.pages) patch.totalPages = pages;
      if (editUnitChanged) { patch.progressUnit = editUnit; patch.currentPage = 0; }
      if (Object.keys(patch).length === 0) {
        setEditOpen(false);
        return;
      }
      await onEditDetails(book.id, patch);
      setEditOpen(false);
    } catch (e: any) {
      setEditError(e?.message || 'Erro ao salvar.');
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => { setSyncInput(String(book.read || 0)); }, [book.read, syncOpen]);

  const doSyncPage = async () => {
    if (!onSyncPage) return;
    const n = parseInt(syncInput, 10);
    if (!Number.isFinite(n) || n < 0 || n > book.pages) {
      setSyncError(`Informe um valor entre 0 e ${book.pages}.`);
      return;
    }
    setSyncSaving(true); setSyncError(null);
    try {
      await onSyncPage(book.id, n);
      setSyncOpen(false);
    } catch (e: any) {
      setSyncError(e.message || 'Erro ao sincronizar');
    } finally {
      setSyncSaving(false);
    }
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setGoalInput(String(book.dailyGoal || 20)); }, [book.dailyGoal]);

  const pct = book.pages > 0 ? Math.round((book.read / book.pages) * 100) : 0;
  const remaining = book.pages - book.read;
  const days = book.targetDate ? Math.max(0, Math.ceil((Date.parse(book.targetDate) - Date.now()) / 86400000)) : null;
  const perDay = days && remaining ? Math.ceil(remaining / days) : book.dailyGoal;
  const daysReading = book.startedAt ? Math.max(0, Math.floor((Date.now() - Date.parse(book.startedAt)) / 86400000)) : null;

  const saveGoal = async () => {
    if (!onUpdateGoal) return;
    const n = parseInt(goalInput, 10);
    if (!Number.isFinite(n) || n < 1 || n > u.goalMax) {
      window.alert(`Meta inválida. Use um número entre 1 e ${u.goalMax}.`);
      return;
    }
    setGoalSaving(true);
    try {
      await onUpdateGoal(book.id, n);
      setEditingGoal(false);
    } catch (e: any) {
      window.alert('Erro: ' + (e.message || e));
    } finally {
      setGoalSaving(false);
    }
  };

  const doSearchCover = async () => {
    if (!onSearchCover) return;
    setCoverBusy('search'); setCoverMsg(null);
    try {
      await onSearchCover(book.id);
      setCoverMsg('Capa encontrada');
      setTimeout(() => { setCoverSheetOpen(false); setCoverMsg(null); }, 1200);
    } catch (e: any) {
      setCoverMsg(e.message || 'Não encontrou capa');
    } finally {
      setCoverBusy(null);
    }
  };

  const doUploadFile = async (file: File) => {
    if (!onUploadCover || !file) return;
    setCoverBusy('upload'); setCoverMsg(null);
    try {
      await onUploadCover(book.id, file);
      setCoverMsg('Capa atualizada');
      setTimeout(() => { setCoverSheetOpen(false); setCoverMsg(null); }, 1200);
    } catch (e: any) {
      setCoverMsg(e.message || 'Erro no upload');
    } finally {
      setCoverBusy(null);
    }
  };

  // Últimas 7 sessões — vira a mini-barra "Ritmo" do card compacto.
  const last7 = [...(book.sessions || [])].sort((a, b) => a.date.localeCompare(b.date)).slice(-7);
  const maxSession = Math.max(1, ...(book.sessions || []).map(s => s.pages));
  // As páginas dos últimos 7 DIAS (não das 7 últimas sessões): é o mesmo
  // número de onde sai o ritmo por dia logo abaixo (pente fino, QA-07).
  const ritmo = ritmoRecente(book.sessions || []);
  const last7Total = ritmo.total;
  const rating = (book as any).rating ?? null;
  const review = (book as any).review ?? null;

  // FP2 — ritmo de leitura real. Janela recente de 7 dias em date-keys SP (robusto
  // a fuso); ETA só quando há ritmo recente sano (≤ 1 ano); velocidade/tempo só pra
  // páginas (un/min não faz sentido em capítulos/posições). O tempo (minutes) só
  // existe em sessões feitas no Modo Foco; o resto degrada gracioso.
  const sess = book.sessions || [];
  const pace = ritmo.porDia; // un/dia, pelos dias que de fato contam (v2/ritmo.ts)
  const paceLabel = pace >= 1 ? String(Math.round(pace)) : pace > 0 ? decimal(pace) : '—';
  const etaRaw = (pace > 0 && remaining > 0) ? Math.ceil(remaining / pace) : null;
  const etaDays = (etaRaw != null && etaRaw <= 365) ? etaRaw : null; // suprime ETA absurda de ritmo lento
  const etaDate = etaDays != null ? new Date(Date.now() + etaDays * 86400000) : null;
  const isPages = (book.progressUnit || 'pages') === 'pages';
  // Só sessões que mediram leitura: 11 páginas registradas em 1 minuto davam
  // "11.0 pág/min" e "~29 min pra terminar" o livro (QA-07).
  const velocity = isPages ? velocidadePlausivel(sess as any) : null; // pág/min
  const timeToFinishMin = velocity ? Math.max(1, Math.round(remaining / velocity)) : null;
  const minPerDayForGoal = (velocity && (book.dailyGoal || 0) > 0) ? Math.max(1, Math.round(book.dailyGoal / velocity)) : null;
  const fmtDur = (m: number) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, '0') : ''}` : `${m}min`;
  const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

  const isVisitor = pageLoaded && pageData && pageData.isOwner === false;

  // Livro mínimo {id} (visitante/deep-link) ainda carregando → skeleton, pra não
  // piscar o layout do dono quebrado.
  if (!pageLoaded && !book.title) {
    return (
      <div className="scroll">
        <TopBar onBack={onBack} title="" />
        <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>Carregando…</div>
      </div>
    );
  }

  // Livro de outra pessoa que não pôde ser carregado (perfil privado/removido).
  if (!book.title && pageLoaded && (!pageData || !pageData.book)) {
    return (
      <div className="scroll">
        <TopBar onBack={onBack} title="" />
        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
          <div className="serif" style={{ fontSize: 22, color: 'var(--ink)', marginBottom: 8 }}>Livro indisponível</div>
          <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>{pageStatus === 404 ? 'Esse livro foi removido.' : 'Talvez este perfil seja privado ou o livro tenha sido removido.'}</div>
        </div>
      </div>
    );
  }

  // ─────────────── ESTADO VISITANTE ───────────────
  if (isVisitor && pageData?.book) {
    const vb = pageData.book;
    const owner = pageData.owner || {};
    const ownerFirst = String(owner.name || 'Leitor').split(' ')[0];
    const vpct = vb.totalPages > 0 ? Math.round((vb.currentPage / vb.totalPages) * 100) : 0;
    const vdays = vb.startedAt ? Math.max(0, Math.floor((Date.now() - Date.parse(vb.startedAt)) / 86400000)) : null;
    const vStatusLabel = STATUSES.find(s => s.id === vb.status)?.label || 'Lendo';
    const vunit = unitMeta(vb.progressUnit);
    return (
      <div className="scroll" style={{ paddingBottom: 60 }}>
        <TopBar onBack={onBack} title={`Estante de ${ownerFirst}`} trailing={
          owner.id ? (
            <button onClick={() => onOpenProfile?.(owner.id)} title="Ver perfil" style={{
              width: 38, height: 38, borderRadius: '50%', background: 'var(--paper-2)', border: '1px solid var(--line)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 0,
            }}>
              {owner.avatarUrl
                ? <img src={owner.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Icon name="user" size={16} />}
            </button>
          ) : undefined
        }/>

        <div style={{ padding: '18px 22px 28px', display: 'flex', flexDirection: 'column', gap: 22 }}>
          {/* IDENTITY (read-only) */}
          <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
            <div style={{ flex: 'none' }}><BookCover book={vb} width={106} height={158} /></div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 2 }}>
              <div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 7, padding: '5px 11px', borderRadius: 99, background: 'rgba(232,112,42,0.10)', border: '1px solid rgba(232,112,42,0.22)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ember)' }} />
                <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#f0a877', fontWeight: 500 }}>{vStatusLabel}</span>
              </div>
              <h1 className="serif" style={{ margin: 0, fontSize: 29, lineHeight: 1.02, color: 'var(--ink)', letterSpacing: '0.2px' }}>{vb.title}</h1>
              <div className="serif" style={{ fontSize: 13.5, color: 'var(--ink-3)', fontStyle: 'italic', marginTop: -2 }}>por {vb.author}</div>
              <div className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 2 }}>{vb.totalPages} {vunit.short}</div>
              {vb.rating ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                  <span style={{ color: 'var(--ember)', fontSize: 14, letterSpacing: '1px' }}>{'★'.repeat(vb.rating)}</span>
                  <span style={{ color: 'var(--paper-3)', fontSize: 14, letterSpacing: '1px' }}>{'★'.repeat(5 - vb.rating)}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 2 }}>{ownerFirst} deu {vb.rating},0</span>
                </div>
              ) : null}
            </div>
          </div>

          {/* PROGRESS glance (read-only) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
              <span style={{ color: '#f0a877' }}>{vStatusLabel}</span>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ color: 'var(--ember)' }}>{vpct}%</span>
              {vdays != null && <><span style={{ opacity: 0.4 }}>·</span><span>há {vdays} {vdays === 1 ? 'dia' : 'dias'}</span></>}
            </div>
            <div style={{ height: 5, borderRadius: 99, background: 'var(--paper-3)', overflow: 'hidden' }}>
              <div style={{ width: `${vpct}%`, height: '100%', borderRadius: 99, background: 'linear-gradient(90deg,#c95f26,var(--ember))' }} />
            </div>
          </div>

          <div style={{ height: 1, background: 'var(--line)' }} />

          {/* RESENHA do dono */}
          <BookReview
            variant="visitor"
            rating={vb.rating ?? null}
            review={vb.review ?? null}
            authorName={owner.name || 'Leitor'}
            authorAvatarUrl={owner.avatarUrl}
            authorId={owner.id}
            ownerFirstName={ownerFirst}
            onOpenProfile={onOpenProfile}
          />

          <div style={{ height: 1, background: 'var(--line)' }} />

          {/* CONVERSA */}
          <BookConversation
            session={session ?? null}
            bookId={book.id}
            variant="visitor"
            canComment={!!pageData.canComment}
            ownerFirstName={ownerFirst}
            myName={myName}
            myAvatarUrl={myAvatarUrl}
            onOpenProfile={onOpenProfile}
            onBlock={onBlockUser}
          />
        </div>
      </div>
    );
  }

  // LIVRO MÍNIMO {id} RESOLVIDO COMO DONO.
  //
  // Este trecho dizia "não acontece pelos callers atuais" — e acontecia. Um
  // livro recém-adicionado pelo Explorar não estava no acervo do cliente, a
  // tela era aberta só com o id, e aqui a pessoa encontrava um "Carregando…"
  // eterno: não havia nada carregando, e nada faria aquele texto sair.
  //
  // A causa foi corrigida em `openBookById`, que agora busca o acervo antes de
  // abrir. Mas a tela não pode voltar a depender de quem a chama: se o livro
  // não veio na prop e o `/page` trouxe, é com o do `/page` que se desenha.
  //
  // `read` não existe na forma do `/page` — lá o campo é `currentPage`. Sem
  // essa tradução o progresso apareceria zerado num livro já começado.
  if (!book.title && pageData?.book) {
    const b = pageData.book;
    book = { ...b, read: b.currentPage ?? 0, pages: b.totalPages ?? 0 } as any;
  }

  // Sem prop e sem `/page`: aí não há livro nenhum, e dizer "carregando" seria
  // mentir. É o fim da linha, com saída.
  if (!book.title) {
    return (
      <div className="scroll">
        <TopBar onBack={onBack} title="" />
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div className="serif" style={{ fontSize: 20, color: 'var(--ink)', marginBottom: 6 }}>
            Não consegui abrir este livro
          </div>
          <p style={{ fontSize: 13.5, color: 'var(--ink-3)', margin: '0 0 16px', lineHeight: 1.5 }}>
            Ele pode ter sido removido, ou a conexão caiu no meio do caminho.
          </p>
          <button onClick={onBack} style={{
            padding: '10px 18px', borderRadius: 999, cursor: 'pointer',
            background: 'transparent', border: '1px solid var(--ember)',
            color: 'var(--ember)', fontSize: 13.5, fontFamily: 'inherit',
          }}>Voltar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="scroll" style={{ paddingBottom: 60 }}>
      <TopBar onBack={onBack} title="Minha estante" trailing={
        <div style={{ position: 'relative' }}>
          <button onClick={() => setMenuOpen(o => !o)} style={{
            width: 38, height: 38, borderRadius: '50%',
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="more" size={16} />
          </button>
          {menuOpen && (
            <div style={{
              position: 'absolute', right: 0, top: 48, zIndex: 30, minWidth: 210,
              background: 'var(--paper)', border: '1px solid var(--line)',
              borderRadius: 14, boxShadow: '0 12px 32px rgba(0,0,0,0.2)', overflow: 'hidden',
            }}>
              {onEditDetails && (
                <button onClick={() => { setMenuOpen(false); setEditOpen(true); }} style={{
                  width: '100%', padding: '14px 16px', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10,
                  borderBottom: '1px solid var(--line)',
                  color: 'var(--ink-2)', fontSize: 14,
                }}>
                  <Icon name="pen" size={16} color="var(--ink-2)"/> Editar dados do livro
                </button>
              )}
              {onSyncPage && (book.sessions || []).length > 0 && (
                <button onClick={() => { setMenuOpen(false); setSyncOpen(true); }} style={{
                  width: '100%', padding: '14px 16px', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10,
                  borderBottom: onDelete ? '1px solid var(--line)' : 'none',
                  color: 'var(--ink-2)', fontSize: 14,
                }}>
                  <Icon name="pen" size={16} color="var(--ink-2)"/> Corrigir página
                </button>
              )}
              {onDelete && (
                <button onClick={() => { setMenuOpen(false); onDelete(); }} style={{
                  width: '100%', padding: '14px 16px', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10,
                  color: 'var(--ember)', fontSize: 14,
                }}>
                  <Icon name="trash" size={16} color="var(--ember)"/> Remover livro
                </button>
              )}
            </div>
          )}
        </div>
      }/>

      <div style={{ padding: '18px 22px 28px', display: 'flex', flexDirection: 'column', gap: 22 }}>

        {/* ── IDENTITY ── */}
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
          <button onClick={() => (onSearchCover || onUploadCover) && setCoverSheetOpen(true)} style={{
            background: 'none', padding: 0, position: 'relative', display: 'block', flex: 'none',
          }}>
            <BookCover book={book} width={106} height={158} />
            {(onSearchCover || onUploadCover) && (
              <span style={{
                position: 'absolute', bottom: 8, right: 8, width: 28, height: 28, borderRadius: '50%',
                background: 'var(--ember)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(232,112,42,0.5)',
              }}>
                <Icon name="camera" size={14} color="#16110d" stroke={2}/>
              </span>
            )}
          </button>

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 2 }}>
            {/* status pill (dropdown) */}
            <div style={{ position: 'relative', alignSelf: 'flex-start' }}>
              <button onClick={() => setStatusOpen(o => !o)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 11px 5px 9px',
                borderRadius: 99, background: 'rgba(232,112,42,0.13)', border: '1px solid rgba(232,112,42,0.32)', cursor: 'pointer',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ember)', boxShadow: '0 0 8px rgba(232,112,42,0.8)' }} />
                <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#f0a877', fontWeight: 500 }}>
                  {STATUSES.find(s => s.id === book.status)?.label || 'Lendo'}
                </span>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="var(--ember)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              {statusOpen && (
                <>
                  <div onClick={() => setStatusOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div style={{ position: 'absolute', left: 0, top: 34, zIndex: 41, minWidth: 150, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 30px rgba(0,0,0,0.4)' }}>
                    {STATUSES.map(s => (
                      <button key={s.id} onClick={() => { setStatusOpen(false); if (s.id !== book.status) onStatusChange(book.id, s.id); }} style={{
                        width: '100%', textAlign: 'left', padding: '11px 14px', background: s.id === book.status ? 'rgba(232,112,42,0.10)' : 'none',
                        border: 'none', borderBottom: '1px solid var(--line)', cursor: 'pointer',
                        fontSize: 13, color: s.id === book.status ? 'var(--ember)' : 'var(--ink-2)',
                      }}>{s.label}</button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* título + lápis */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <h1 className="serif" style={{ margin: 0, fontSize: 29, lineHeight: 1.02, color: 'var(--ink)', letterSpacing: '0.2px' }}>{book.title}</h1>
              {onEditDetails && <PencilBtn onClick={() => setEditOpen(true)} style={{ marginTop: 8 }} />}
            </div>

            {/* autor + lápis */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: -2 }}>
              <span className="serif" style={{ fontSize: 13.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>por {book.author}</span>
              {onEditDetails && <PencilBtn size={10} onClick={() => setEditOpen(true)} />}
            </div>

            {/* páginas + lápis */}
            <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 2 }}>
              <span>{book.pages} {u.short}</span>
              {book.highlights > 0 && <><span style={{ opacity: 0.45 }}>·</span><span>{book.highlights} grifos</span></>}
              {onEditDetails && <PencilBtn size={9} onClick={() => setEditOpen(true)} />}
            </div>

            {/* nota (review rating) */}
            <button onClick={() => reviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <span style={{ color: 'var(--ember)', fontSize: 14, letterSpacing: '1px' }}>{'★'.repeat(rating || 0)}</span>
              <span style={{ color: 'var(--paper-3)', fontSize: 14, letterSpacing: '1px' }}>{'★'.repeat(5 - (rating || 0))}</span>
              {rating ? <span style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 2 }}>{rating},0</span> : <span style={{ fontSize: 12, color: 'var(--ink-4)', marginLeft: 2 }}>dar nota</span>}
              <PencilBtn size={10} />
            </button>
          </div>
        </div>

        {/* ── PROGRESS compact ── */}
        <div style={{ background: 'rgba(255,255,255,0.028)', border: '1px solid var(--line)', borderRadius: 18, padding: '17px 18px 16px', display: 'flex', flexDirection: 'column', gap: 13 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span className="serif" style={{ fontSize: 32, color: 'var(--ink)', lineHeight: 1 }}>{book.read}</span>
              <span className="serif" style={{ fontSize: 18, color: 'var(--ink-4)' }}>/ {book.pages}</span>
            </div>
            <span className="serif" style={{ fontSize: 30, color: 'var(--ember)', lineHeight: 1 }}>{pct}%</span>
          </div>
          <div style={{ height: 7, borderRadius: 99, background: 'var(--paper-3)', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: 'linear-gradient(90deg,#c95f26,var(--ember))', transition: 'width 400ms' }} />
          </div>
          <div className="mono" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
            <span>{remaining} {u.short} restantes</span>
            {daysReading != null && <span style={{ color: 'var(--ink-3)' }}>{daysReading === 0 ? 'começou hoje' : `há ${daysReading} ${daysReading === 1 ? 'dia' : 'dias'}`}</span>}
          </div>

          <div style={{ height: 1, background: 'var(--line)', margin: '1px 0' }} />

          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            {/* meta diária (editável) */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Meta diária</span>
                {onUpdateGoal && !editingGoal && <PencilBtn size={9} onClick={() => setEditingGoal(true)} />}
              </div>
              {editingGoal ? (
                <div style={{ marginTop: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" value={goalInput} onChange={e => setGoalInput(e.target.value)} min={1} max={u.goalMax} autoFocus
                      style={{ width: 54, background: 'var(--paper)', border: '1px solid var(--ember)', borderRadius: 8, padding: '4px 6px', fontSize: 18, fontFamily: 'Instrument Serif, serif', color: 'var(--ink)', textAlign: 'center' }} />
                    <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{u.short}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
                    <button onClick={() => { setEditingGoal(false); setGoalInput(String(book.dailyGoal)); }} disabled={goalSaving} style={{ padding: '4px 8px', borderRadius: 8, background: 'var(--paper-3)', color: 'var(--ink-2)', fontSize: 11, border: 'none' }}>Cancelar</button>
                    <button onClick={saveGoal} disabled={goalSaving} style={{ padding: '4px 8px', borderRadius: 8, background: 'var(--ember)', color: '#16110d', fontSize: 11, fontWeight: 600, border: 'none' }}>{goalSaving ? '…' : 'OK'}</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>{book.dailyGoal}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{u.short}/dia</span>
                </div>
              )}
              {!editingGoal && perDay !== book.dailyGoal && (
                <div style={{ fontSize: 10, color: 'var(--ink-4)' }}>precisa {perDay}/dia no prazo</div>
              )}
            </div>

            <div style={{ width: 1, background: 'var(--line)', margin: '0 16px' }} />

            {/* ritmo · 7 dias */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Ritmo · 7 dias</span>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 9 }}>
                <span className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>{last7Total}</span>
                {last7.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2.5, height: 20, paddingBottom: 3 }}>
                    {last7.map((s, i) => {
                      const h = Math.max(5, Math.round((s.pages / maxSession) * 17));
                      const isMax = s.pages === maxSession;
                      return <span key={i} style={{ width: 4, height: h, borderRadius: 1.5, background: isMax ? 'var(--ember)' : 'rgba(232,112,42,0.55)' }} />;
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── FP2: Quanto falta pra terminar (ritmo de leitura real) ── */}
        {book.status === 'reading' && remaining > 0 && sess.length > 0 && (
          <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 18, padding: '15px 16px', marginTop: 12, display: 'flex', flexDirection: 'column', gap: 13 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="mono" style={{ fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Quanto falta pra terminar</span>
              <span className="mono" style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>no ritmo atual</span>
            </div>

            {etaDays != null ? (
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                  <span className="serif" style={{ fontSize: 42, lineHeight: 0.9, color: 'var(--ink)' }}>{etaDays}</span>
                  <span className="serif" style={{ fontSize: 19, color: 'var(--ink-3)' }}>{etaDays === 1 ? 'dia' : 'dias'}</span>
                </div>
                {etaDate && (
                  <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--ink-4)', lineHeight: 1.4 }}>
                    termina por volta de<br /><span className="serif" style={{ fontSize: 17, color: 'var(--ember-2)' }}>{etaDate.getDate()} de {MESES[etaDate.getMonth()]}{etaDate.getFullYear() !== new Date().getFullYear() ? ` de ${etaDate.getFullYear()}` : ''}</span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{pace > 0 ? 'Nesse ritmo ainda falta um bom caminho — bora ler mais um pouco?' : 'Volte a ler que eu estimo quando você termina.'}</div>
            )}

            <div style={{ height: 1, background: 'var(--line)' }} />

            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Ritmo</span>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>{paceLabel}</span>
                  <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{u.short}/dia</span>
                </div>
              </div>
              <div style={{ width: 1, background: 'var(--line)', margin: '0 14px' }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Faltam</span>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>{remaining}</span>
                  <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{u.short}</span>
                </div>
              </div>
              {velocity && (
                <>
                  <div style={{ width: 1, background: 'var(--line)', margin: '0 14px' }} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Velocidade</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>{decimal(velocity)}</span>
                      <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{u.short}/min</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {velocity && timeToFinishMin != null ? (
              <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="clock" size={13} color="var(--ember-2)" />
                <span>~<strong style={{ color: 'var(--ink-2)' }}>{fmtDur(timeToFinishMin)}</strong> de leitura pra terminar{minPerDayForGoal ? <> · <strong style={{ color: 'var(--ink-2)' }}>~{minPerDayForGoal} min/dia</strong> pra bater a meta</> : null}</span>
              </div>
            ) : (
              <div style={{ fontSize: 11.5, color: 'var(--ink-4)', lineHeight: 1.4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="clock" size={13} color="var(--ink-4)" />
                <span>Use o <strong style={{ color: 'var(--ember-2)' }}>Modo Foco</strong> pra ver sua velocidade e o tempo restante.</span>
              </div>
            )}
          </div>
        )}

        {/* ── ação principal (inline) ── */}
        {book.status === 'reading' && (
          <button onClick={onLogRead} style={{
            width: '100%', border: 'none', cursor: 'pointer', borderRadius: 16, padding: 16,
            background: 'linear-gradient(180deg,#ef7e36,#df6a26)', color: '#1a120c', fontSize: 15, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
            boxShadow: '0 12px 30px -8px rgba(232,112,42,0.6)',
          }}>
            <Icon name="bookmark" size={17} color="#1a120c" stroke={2}/>
            Registrar leitura
          </button>
        )}
        {book.status === 'wishlist' && (
          <button onClick={() => onStatusChange(book.id, 'reading')} style={{
            width: '100%', background: 'var(--ink)', color: 'var(--paper)', border: 'none', cursor: 'pointer',
            padding: 16, borderRadius: 16, fontSize: 15, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <Icon name="book" size={17} color="var(--paper)" stroke={2}/>
            Começar a ler
          </button>
        )}
        {book.status === 'paused' && (
          <button onClick={() => onStatusChange(book.id, 'reading')} style={{
            width: '100%', background: 'var(--paper-2)', color: 'var(--ink)', border: '1px solid var(--line)', cursor: 'pointer',
            padding: 14, borderRadius: 16, fontSize: 14, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <Icon name="book" size={16} color="var(--ink)" stroke={2}/>
            Retomar leitura
          </button>
        )}

        {/* ── FP5: Mapa de leitura (só aparece quando há leitura por trechos) ── */}
        <ReadingMap book={book} />

        {/* ── Caderno de estudos (grifos, notas, ideias, áudios) ── */}
        {onOpenStudy && book.status !== 'wishlist' && (
          <button onClick={onOpenStudy} style={{
            width: '100%', marginTop: 10, border: `1px solid ${mentorNovas > 0 ? 'var(--ember)' : 'var(--line-strong)'}`, background: 'var(--paper-2)', color: 'var(--ink)', cursor: 'pointer',
            borderRadius: 16, padding: 14, fontSize: 14.5, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
          }}>
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z"/><path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5A1.5 1.5 0 0 0 20 18.5z"/></svg>
              <BadgeMentor n={mentorNovas} style={{ top: -9, right: -12 }} />
            </span>
            Caderno de estudos
            {mentorNovas > 0 && <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ember)' }}>· {mentorNovas === 1 ? '1 nota nova do Mentor' : `${mentorNovas} notas novas do Mentor`}</span>}
          </button>
        )}

        <div style={{ height: 1, background: 'var(--line)' }} />

        {/* ── RESENHA ── */}
        <div ref={reviewRef}>
          <BookReview
            variant="owner"
            rating={rating}
            review={review}
            authorName="você"
            authorAvatarUrl={myAvatarUrl}
            canEdit={!!onSaveReview}
            onSave={(r, t) => onSaveReview?.(book.id, r, t)}
            onOpenProfile={onOpenProfile}
          />
        </div>

        <div style={{ height: 1, background: 'var(--line)' }} />

        {/* ── CONVERSA ── */}
        <BookConversation
          session={session ?? null}
          bookId={book.id}
          variant="owner"
          canComment={true}
          ownerFirstName={(myName || 'você').split(' ')[0]}
          myName={myName}
          myAvatarUrl={myAvatarUrl}
          onOpenProfile={onOpenProfile}
          onBlock={onBlockUser}
        />
      </div>

      <Sheet open={coverSheetOpen} onClose={() => !coverBusy && setCoverSheetOpen(false)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Capa do livro</div>
            <div className="serif" style={{ fontSize: 26 }}>Alterar capa</div>
          </div>
          <button onClick={() => !coverBusy && setCoverSheetOpen(false)} style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--paper-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Icon name="close" size={16}/></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {onSearchCover && (
            <button onClick={doSearchCover} disabled={!!coverBusy} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px', borderRadius: 16,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              textAlign: 'left',
              opacity: coverBusy && coverBusy !== 'search' ? 0.4 : 1,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: 'var(--ember-soft)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}><Icon name="search" size={20} color="var(--ember)"/></div>
              <div style={{ flex: 1 }}>
                <div className="serif" style={{ fontSize: 17, lineHeight: 1.1 }}>
                  {coverBusy === 'search' ? 'Buscando capa…' : 'Buscar capa automática'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                  Procurar via Google Books pelo título e autor
                </div>
              </div>
              <Icon name="chevron-right" size={16} color="var(--ink-4)"/>
            </button>
          )}
          {onUploadCover && <>
            <button onClick={() => cameraInputRef.current?.click()} disabled={!!coverBusy} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px', borderRadius: 16,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              textAlign: 'left',
              opacity: coverBusy && coverBusy !== 'upload' ? 0.4 : 1,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: 'var(--ember-soft)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}><Icon name="camera" size={20} color="var(--ember)"/></div>
              <div style={{ flex: 1 }}>
                <div className="serif" style={{ fontSize: 17, lineHeight: 1.1 }}>Tirar foto da capa</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Usar a câmera agora</div>
              </div>
              <Icon name="chevron-right" size={16} color="var(--ink-4)"/>
            </button>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) doUploadFile(f);
                e.currentTarget.value = '';
              }}
              style={{ display: 'none' }}
            />

            <button onClick={() => fileInputRef.current?.click()} disabled={!!coverBusy} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px', borderRadius: 16,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              textAlign: 'left',
              opacity: coverBusy && coverBusy !== 'upload' ? 0.4 : 1,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: 'var(--ember-soft)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}><Icon name="arrow-up" size={20} color="var(--ember)"/></div>
              <div style={{ flex: 1 }}>
                <div className="serif" style={{ fontSize: 17, lineHeight: 1.1 }}>
                  {coverBusy === 'upload' ? 'Enviando...' : 'Enviar da galeria'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Escolher imagem do dispositivo</div>
              </div>
              <Icon name="chevron-right" size={16} color="var(--ink-4)"/>
            </button>
            <input ref={fileInputRef} type="file" accept="image/*"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) doUploadFile(f);
                e.currentTarget.value = '';
              }}
              style={{ display: 'none' }}
            />
          </>}
        </div>

        {coverMsg && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 12,
            background: 'var(--ember-soft)', color: 'var(--ember)',
            fontSize: 13, textAlign: 'center',
          }}>{coverMsg}</div>
        )}
      </Sheet>

      {/* Sheet — Sincronizar página atual */}
      <Sheet open={syncOpen} onClose={() => !syncSaving && setSyncOpen(false)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Onde você parou</div>
            <div className="serif" style={{ fontSize: 26 }}>Onde você parou?</div>
          </div>
          <button onClick={() => !syncSaving && setSyncOpen(false)} style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--paper-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Icon name="close" size={16}/></button>
        </div>

        <div style={{
          padding: '12px 16px', borderRadius: 14,
          background: 'var(--paper-2)', border: '1px solid var(--line)',
          fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.45,
          marginTop: 6,
        }}>
          Use quando você já leu este livro fora do app. Ao salvar, o app passa a contar as próximas leituras <strong style={{ color: 'var(--ink-2)' }}>a partir desta página</strong> — <strong style={{ color: 'var(--ink-2)' }}>sem somar como leitura de hoje</strong>. Seu histórico é preservado.
        </div>

        <div style={{
          background: 'var(--paper-2)', borderRadius: 22,
          padding: '28px 20px', marginTop: 16,
          border: '1px solid var(--line)', textAlign: 'center',
        }}>
          <div className="mono" style={{
            fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'var(--ink-3)', marginBottom: 12,
          }}>Estou agora em</div>
          <input
            type="number"
            min={0} max={book.pages}
            value={syncInput}
            onChange={e => setSyncInput(e.target.value)}
            style={{
              width: 180, fontFamily: 'Instrument Serif, serif',
              fontSize: 72, lineHeight: 1, letterSpacing: '-0.03em',
              color: 'var(--ember)', background: 'transparent',
              border: 'none', textAlign: 'center', outline: 'none',
            }}
          />
          <div className="mono" style={{
            fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'var(--ink-3)', marginTop: 4,
          }}>de {book.pages} {u.short}</div>
        </div>

        {syncError && (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 12,
            background: 'rgba(232,122,59,0.12)', color: 'var(--ember)',
            fontSize: 13, textAlign: 'center',
          }}>{syncError}</div>
        )}

        <button onClick={doSyncPage} disabled={syncSaving} style={{
          width: '100%', marginTop: 18,
          background: syncSaving ? 'var(--paper-3)' : 'var(--ink)',
          color: syncSaving ? 'var(--ink-4)' : 'var(--paper)',
          padding: '16px', borderRadius: 16, fontSize: 15, fontWeight: 500,
        }}>
          {syncSaving ? 'Salvando...' : 'Salvar minha página'}
        </button>
      </Sheet>

      {/* Sheet de editar dados do livro — abre via menu "..." → "Editar dados". */}
      <Sheet open={editOpen} onClose={() => !editSaving && setEditOpen(false)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Editar</div>
            <div className="serif" style={{ fontSize: 26 }}>Dados do livro</div>
          </div>
          <button onClick={() => !editSaving && setEditOpen(false)} style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--paper-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Icon name="close" size={16}/></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
          <div>
            <div className="mono" style={{
              fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginBottom: 6,
            }}>Título</div>
            <input
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              placeholder="Ex: Discipulado"
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 14,
                border: '1px solid var(--line)',
                background: 'var(--paper-2)',
                fontSize: 15, fontFamily: 'inherit', color: 'var(--ink)',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <div className="mono" style={{
              fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginBottom: 6,
            }}>Autor</div>
            <input
              value={editAuthor}
              onChange={e => setEditAuthor(e.target.value)}
              placeholder="Ex: Dietrich Bonhoeffer"
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 14,
                border: '1px solid var(--line)',
                background: 'var(--paper-2)',
                fontSize: 15, fontFamily: 'inherit', color: 'var(--ink)',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <div className="mono" style={{
              fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginBottom: 6,
            }}>Medir progresso por</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              {UNIT_OPTIONS.map(o => (
                <button key={o.value} type="button" onClick={() => setEditUnit(o.value)} style={{
                  flex: 1, padding: '10px 6px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: editUnit === o.value ? 'var(--ember-soft)' : 'var(--paper-2)',
                  color: editUnit === o.value ? 'var(--ember-2)' : 'var(--ink-3)',
                  border: '1px solid ' + (editUnit === o.value ? 'var(--ember)' : 'var(--line)'),
                }}>{o.label}</button>
              ))}
            </div>
            <div className="mono" style={{
              fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginBottom: 6,
            }}>{eu.totalLabel}</div>
            <input
              type="number" inputMode="numeric"
              min={1} max={500000}
              value={editPages}
              onChange={e => setEditPages(e.target.value)}
              placeholder="Ex: 312"
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 14,
                border: '1px solid var(--line)',
                background: 'var(--paper-2)',
                fontSize: 15, fontFamily: 'inherit', color: 'var(--ink)',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
            {editUnitChanged && (
              <div style={{ fontSize: 11.5, color: 'var(--ember-2)', marginTop: 8, lineHeight: 1.4 }}>
                Ao trocar a unidade, sua posição volta pra 0 — depois registre de novo onde você parou.
              </div>
            )}
          </div>
        </div>

        {editError && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 12,
            background: 'rgba(199,83,26,0.10)', border: '1px solid rgba(199,83,26,0.22)',
            color: 'var(--ember)', fontSize: 13, lineHeight: 1.4,
          }}>
            {editError}
          </div>
        )}

        <button onClick={doSaveDetails} disabled={editSaving} style={{
          width: '100%', marginTop: 18,
          background: editSaving ? 'var(--paper-3)' : 'var(--ink)',
          color: editSaving ? 'var(--ink-4)' : 'var(--paper)',
          padding: '16px', borderRadius: 16, fontSize: 15, fontWeight: 500,
        }}>
          {editSaving ? 'Salvando...' : 'Salvar alterações'}
        </button>
      </Sheet>
    </div>
  );
};

/* LogReadingSheet — user digits the page they stopped at (not quantity) */
// FP5 · Mapa de leitura — barra picada (união das faixas) + % de cobertura +
// lista colapsável de trechos. Só renderiza quando o livro tem leitura por trechos
// (leitura linear pura continua sem poluição visual). Cobertura = união de páginas
// únicas; o "esforço" (meta/streak) é contado em separado pelas sessões.
function ReadingMap({ book }: { book: BookV2 }) {
  const [open, setOpen] = useState(false);
  const u = unitMeta(book.progressUnit);
  const total = book.pages || 0;
  const sessions: any[] = (book as any).sessions || [];
  const trechos = sessions
    .filter(s => s && s.kind === 'trecho' && s.segFrom != null && s.segTo != null)
    .map(s => ({ from: s.segFrom, to: Math.min(s.segTo, total || s.segTo), label: s.segLabel || null, linear: false }));
  if (!trechos.length || total <= 0) return null;

  const read = Math.min(book.read || 0, total);
  const intervals = [
    ...(read >= 1 ? [{ from: 1, to: read, linear: true, label: null as string | null }] : []),
    ...trechos,
  ].filter(iv => iv.to >= iv.from);

  // União pra cobertura e pra barra (páginas únicas; trechos sobrepostos não dobram)
  const sorted = intervals.map(iv => ({ from: iv.from, to: iv.to })).sort((a, b) => a.from - b.from);
  const merged: { from: number; to: number }[] = [];
  for (const iv of sorted) {
    const last = merged[merged.length - 1];
    if (last && iv.from <= last.to + 1) last.to = Math.max(last.to, iv.to);
    else merged.push({ ...iv });
  }
  const covered = merged.reduce((s, iv) => s + (iv.to - iv.from + 1), 0);
  const pctCov = Math.round((covered / total) * 100);
  const listItems = intervals.slice().sort((a, b) => a.from - b.from);

  return (
    <div style={{ marginTop: 10, border: '1px solid var(--line)', borderRadius: 16, background: 'var(--paper-2)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', padding: '14px 14px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 9 }}>
          <span className="mono" style={{ fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Mapa de leitura</span>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}><strong style={{ color: 'var(--ember)' }}>{pctCov}%</strong> · {covered} de {total} {u.short}</span>
        </div>
        <div style={{ position: 'relative', height: 13, borderRadius: 7, background: 'var(--paper-3)', overflow: 'hidden', border: '1px solid var(--line)' }}>
          {merged.map((iv, i) => (
            <div key={i} style={{ position: 'absolute', top: 0, bottom: 0, left: `${((iv.from - 1) / total) * 100}%`, width: `${((iv.to - iv.from + 1) / total) * 100}%`, background: 'var(--ember)' }}/>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 9.5, color: 'var(--ink-4)' }}>
          <span>pág. 1</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--ink-3)' }}>{open ? 'ocultar' : `${listItems.length} ${listItems.length === 1 ? 'trecho' : 'trechos'}`} <Icon name={open ? 'chevron-down' : 'chevron-right'} size={11} color="var(--ink-3)"/></span>
          <span>pág. {total}</span>
        </div>
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--line)', padding: '8px 14px 12px' }}>
          {listItems.map((iv, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 0' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ember)', flex: 'none' }}/>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                pág. {iv.from}–{iv.to}<span style={{ color: 'var(--ink-3)' }}> · {iv.linear ? 'leitura contínua' : (iv.label || 'trecho')}</span>
              </span>
              <span style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap', flex: 'none' }}>{iv.to - iv.from + 1} {u.short}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// O número grande da folha "Até onde você leu?": toca e digita a página, com o
// teclado numérico do celular. Cada dígito já move a régua; fora da faixa vira
// o limite (0 ou o total).
const NumeroPagina: React.FC<{ page: number; max: number; rotulo: string; onChange: (p: number) => void }> = ({ page, max, rotulo, onChange }) => {
  const [rascunho, setRascunho] = useState<string | null>(null);
  return (
    <input
      type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="off" enterKeyHint="done"
      className="serif" data-pagina-digitar aria-label={rotulo}
      value={rascunho ?? String(page)}
      onFocus={e => { setRascunho(String(page)); e.currentTarget.select(); }}
      onChange={e => {
        const d = e.target.value.replace(/\D/g, '').slice(0, 5);
        setRascunho(d);
        if (d !== '') onChange(Math.min(max, parseInt(d, 10)));
      }}
      onBlur={() => setRascunho(null)}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      style={{
        display: 'block', width: '100%', padding: 0, margin: 0,
        fontSize: 96, lineHeight: 1, letterSpacing: '-0.03em', color: 'var(--ember)',
        background: 'transparent', border: 'none', textAlign: 'center', outline: 'none',
        caretColor: 'var(--ember)', cursor: 'text',
      }}
    />
  );
};

// A régua de páginas: alça grande (área de toque de 44 px) e, na primeira vez
// que a folha abre neste aparelho, um pulso leve na alça — quem só via os
// botões -10/+10 gastava 7 toques para chegar à p. 52.
const DICA_REGUA = 'forjamente.regua.dica';
const ReguaPagina: React.FC<{ page: number; max: number; step: number; rotulo: string; onChange: (p: number) => void }> = ({ page, max, step, rotulo, onChange }) => {
  const [pulsa, setPulsa] = useState(false);
  useEffect(() => {
    try {
      if (localStorage.getItem(DICA_REGUA)) return;
      localStorage.setItem(DICA_REGUA, '1');
      setPulsa(true);
    } catch { /* sem localStorage: sem dica */ }
  }, []);
  const f = max > 0 ? page / max : 0;
  return (
    <div className="regua-caixa" style={{ ['--regua-f' as any]: f, ['--regua-p' as any]: `${f * 100}%` }}>
      <input type="range" min={0} max={max} step={step} value={page}
        className="regua-pagina" data-regua data-regua-pulsa={pulsa ? '' : undefined} aria-label={rotulo}
        onChange={e => onChange(+e.target.value)}
        onPointerDown={() => setPulsa(false)}/>
      {/* O Chrome não anima a alça nativa: o pulso é um halo por cima dela. */}
      {pulsa && <span className="regua-halo" aria-hidden onAnimationEnd={() => setPulsa(false)}/>}
    </div>
  );
};

export const LogReadingSheet: React.FC<{
  open: boolean;
  book: BookV2 | null;
  onClose: () => void;
  /** Devolve festa=true quando o App vai comemorar este salvamento (livro
   *  terminado, meta, protocolo): aí a folha não festeja por cima. */
  onSave: (bookId: string, newPage: number, minutes?: number | null) => Promise<{ festa?: boolean } | void>;
  /** FP5 — registra um TRECHO (da/até); não move o cursor linear ("de onde parei"). */
  onSaveSegment?: (bookId: string, from: number, to: number, minutes?: number | null, label?: string | null) => Promise<void>;
  /** Atalho pra a página "Minhas leituras" (editar/excluir registros). */
  onOpenReadings?: () => void;
  /** Atalho pra sincronizar a página inicial (caso user já tinha lido fora do app). */
  onSyncPage?: (bookId: string, page: number) => Promise<void> | void;
  /** Abre o caderno de estudos do livro, ancorado na página recém-registrada. */
  onOpenStudy?: (bookId: string, page: number) => void;
  /** Por onde a folha abre. O card do Protocolo pede 'foco' (o check-in do dia
   *  é com o cronômetro); o "Registrar leitura" de sempre abre em 'linear'. */
  modoInicial?: 'linear' | 'foco';
  /** Uma celebração do App está na tela: a folha some sem perder o estado e
   *  volta quando ela fecha (pente fino, UX-06). */
  pausado?: boolean;
  /** Reabriu o app com uma sessão de foco guardada e pediu "Registrar" (QA-20). */
  registrarSessaoSalva?: boolean;
  /** Mentor do Livro: quantas notas o último registro deste livro liberou. Com
   *  mais de zero, o fim do registro convida a ler (no lugar de "Anote"). */
  trilhaNovas?: number;
  onOpenTrilha?: (bookId: string) => void;
  onDispensarTrilha?: () => void;
  /** Este livro tem a Travessia: o convite fala do caminho que abriu. */
  travessia?: boolean;
  /** Interface nova: sem nota nova para anunciar, o registro termina no mapa
   *  (o caminho andou junto com a página) em vez do convite de anotar. */
  aposSalvarNoMapa?: (bookId: string) => void;
}> = ({ open, book, onClose, onSave, onSaveSegment, onOpenReadings, onSyncPage, onOpenStudy, modoInicial = 'linear', pausado = false, registrarSessaoSalva = false, trilhaNovas = 0, onOpenTrilha, onDispensarTrilha, travessia = false, aposSalvarNoMapa }) => {
  const u = unitMeta(book?.progressUnit);
  const [page, setPage] = useState<number>(book?.read ?? 0);
  const [celebrating, setCelebrating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Convite "Anote o que você leu" — aparece após registrar (se houver caderno)
  const [annotateInvite, setAnnotateInvite] = useState(false);
  const [annotatePage, setAnnotatePage] = useState(0);
  // O App só fica sabendo das notas liberadas quando o registro volta do
  // servidor; o fim da comemoração lê o valor daquele momento.
  const [conviteTrilha, setConviteTrilha] = useState(0);
  const trilhaNovasRef = useRef(0);
  trilhaNovasRef.current = trilhaNovas;
  // Modo sincronizar: o mesmo Sheet vira o input "em que pagina voce esta?"
  // sem precisar abrir outro modal por cima. Quando syncSaving termina, volta
  // ao modo registro normal com o novo book.read ja refletido pela prop.
  const [syncMode, setSyncMode] = useState(false);
  const [syncInput, setSyncInput] = useState<string>(String(book?.read ?? 0));
  const [syncSaving, setSyncSaving] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  // FP5: modo de registro — linear ("de onde parei"), foco (cronômetro), trecho (da/até)
  // MODO FOCO É A ABERTURA, não uma escolha.
  //
  // O cronômetro já tinha saído de um menu suspenso pra virar botão visível
  // (ver o comentário nos três modos, mais abaixo) porque escondido ele não era
  // usado. Mas continuava custando um toque e uma decisão ANTES de ler — e
  // decidir na hora de executar é o que atrapalha hábito.
  //
  // Agora o modal abre no cronômetro. Os três modos seguem ali: quem quiser só
  // lançar a página troca num toque, e quem termina a sessão de foco continua
  // escolhendo entre "onde parei" e "um trecho" DEPOIS de ler, que é quando a
  // resposta já se sabe.
  //
  // Pente fino 03/10 (UX-03): quem abre "Registrar" quase sempre já leu e só
  // quer lançar a página — o cronômetro na frente custava um toque a mais e
  // parecia obrigatório. Abre em "De onde parei"; o Modo foco continua a um
  // toque, no mesmo seletor.
  const [mode, setMode] = useState<'linear' | 'foco' | 'trecho'>(modoInicial);
  useEffect(() => { if (open) setMode(modoInicial); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [focusMinutes, setFocusMinutes] = useState<number | null>(null);
  const [focusSeconds, setFocusSeconds] = useState<number | null>(null);
  // Trecho: faixa lida (da/até) + rótulo opcional
  const [segFrom, setSegFrom] = useState<number>(1);
  const [segTo, setSegTo] = useState<number>(1);
  const [segLabel, setSegLabel] = useState('');
  const [segSaving, setSegSaving] = useState(false);
  // PÁGINA DE INÍCIO DO PRIMEIRO REGISTRO (14/10). Sem ela, todo livro começava
  // do 0: quem estava na p. 380 e registrava "parei na 392" ganhava 392
  // páginas num registro só. Antes do primeiro registro de um livro (sem
  // leitura e na p. 0) a folha pergunta onde a pessoa está; essa página vira o
  // ponto de partida pelo sincronizar, que não conta como leitura.
  const [inicioDefinido, setInicioDefinido] = useState(false);
  const [inicioInput, setInicioInput] = useState('');
  const [inicioSalvando, setInicioSalvando] = useState(false);
  const [inicioErro, setInicioErro] = useState<string | null>(null);
  // Trava do toque duplo: o `saving` só vale depois do próximo render, e dois
  // toques seguidos chegavam a mandar a mesma leitura duas vezes.
  const enviandoRef = useRef(false);
  useEffect(() => {
    setInicioDefinido(false); setInicioInput(''); setInicioErro(null); setInicioSalvando(false);
  }, [open, book?.id]);

  // Reset de tudo quando o modal fecha ou troca de livro
  useEffect(() => {
    if (!open) {
      setSyncMode(false); setSyncError(null); setSyncSaving(false);
      setMode('linear');
      setFocusMinutes(null); setFocusSeconds(null); setSegLabel(''); setSegSaving(false);
      setAnnotateInvite(false);
      setConviteTrilha(0);
    }
    setSyncInput(String(book?.read ?? 0));
  }, [open, book?.id, book?.read]);

  const doSync = async () => {
    if (!book || !onSyncPage || enviandoRef.current) return;
    const n = parseInt(syncInput, 10);
    if (!Number.isFinite(n) || n < 0 || n > book.pages) {
      setSyncError(`Informe um valor entre 0 e ${book.pages}.`);
      return;
    }
    setSyncSaving(true); setSyncError(null);
    enviandoRef.current = true;
    try {
      await onSyncPage(book.id, n);
      // Sucesso: sai do syncMode, atualiza page pra refletir nova posicao,
      // user fica no modo registro pronto pra salvar a sessao
      setSyncMode(false);
      setPage(n);
    } catch (e: any) {
      setSyncError(e?.message || 'Erro ao sincronizar');
    } finally {
      setSyncSaving(false);
      enviandoRef.current = false;
    }
  };

  // Define a página de início. Zero é "comecei o livro agora" e não precisa
  // de servidor; qualquer outra vira o marcador de sincronização.
  const definirInicio = async (valor?: number) => {
    if (!book || enviandoRef.current) return;
    const n = valor ?? parseInt(inicioInput, 10);
    if (!Number.isFinite(n) || n < 0 || n > book.pages) {
      setInicioErro(`Informe um número entre 0 e ${book.pages}.`);
      return;
    }
    if (n === book.pages) {
      setInicioErro(`Essa é a última ${u.where}. Se já terminou, marque o livro como lido.`);
      return;
    }
    setInicioErro(null);
    if (n > 0 && onSyncPage) {
      enviandoRef.current = true; setInicioSalvando(true);
      try { await onSyncPage(book.id, n); }
      catch (e: any) { setInicioErro(e?.message || 'Não consegui guardar a página. Tente de novo.'); return; }
      finally { enviandoRef.current = false; setInicioSalvando(false); }
    }
    setPage(n);
    setInicioDefinido(true);
  };

  useEffect(() => {
    if (book && open) {
      setPage(book.read); setCelebrating(false); setError(null);
      // FP5: trecho começa sugerindo a próxima página depois de onde você parou
      const start = Math.min((book.read || 0) + 1, Math.max(1, book.pages || 1));
      setSegFrom(start); setSegTo(start);
    }
  }, [book, open]);

  if (!book) return null;

  const delta = page - book.read;
  // Primeiro registro: o livro ainda não tem leitura e está na p. 0.
  const semHistorico = !(book.sessions || []).some((ss: any) => (ss.pages || 0) > 0);
  const pedirInicio = mode === 'linear' && !syncMode && !inicioDefinido && (book.read || 0) === 0 && semHistorico && !!onSyncPage;
  // "p. 380" / "cap. 3" / "pos. 1200" — o registro diz de onde até onde.
  const abrev = u.unit === 'chapters' ? 'cap.' : u.unit === 'positions' ? 'pos.' : 'p.';
  const ref = (n: number) => `${abrev} ${n}`;
  const daAte = u.unit === 'chapters' ? `Do ${ref(book.read)} até o ${abrev}` : `Da ${ref(book.read)} até a ${abrev}`;
  const pct = book.pages > 0 ? Math.round((page / book.pages) * 100) : 0;
  const finished = page >= book.pages;
  // FP5: páginas do trecho (da/até, inclusive) e "delta efetivo" do modo atual
  const segPages = (segTo >= segFrom) ? (segTo - segFrom + 1) : 0;
  const effDelta = mode === 'trecho' ? segPages : delta;
  // Modo Foco: velocidade da sessão e "quanto falta" no ritmo atual (só faz sentido no linear)
  const focusSpeed = (focusSeconds && effDelta > 0) ? effDelta / (focusSeconds / 60) : (focusMinutes && effDelta > 0) ? effDelta / focusMinutes : null; // unidades/min
  const toFinishMin = (mode === 'linear' && focusSpeed) ? Math.round((book.pages - page) / focusSpeed) : null;
  const perDayMin = (mode === 'linear' && focusSpeed) ? Math.round((book.dailyGoal || 0) / focusSpeed) : null;

  const inc = (n: number) => setPage(p => Math.min(book.pages, Math.max(0, p + n)));

  const handleSave = async () => {
    if (delta <= 0 || saving || enviandoRef.current) return;
    enviandoRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const r = await onSave(book.id, page, focusMinutes);
      const savedAtPage = page;
      setFocusMinutes(null); setFocusSeconds(null);
      const seguir = () => {
        setCelebrating(false);
        if (trilhaNovasRef.current > 0 && onOpenTrilha) setConviteTrilha(trilhaNovasRef.current);
        else if (travessia && aposSalvarNoMapa) { aposSalvarNoMapa(book.id); onClose(); }
        else if (onOpenStudy) { setAnnotatePage(savedAtPage); setAnnotateInvite(true); }
        else onClose();
      };
      // Uma comemoração por salvamento: se o App vai festejar, a folha pula a
      // dela e já fica no passo seguinte (volta com ele quando a festa fecha).
      if (r && r.festa) seguir();
      else { setCelebrating(true); setTimeout(seguir, 1200); }
    } catch (e: any) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
      enviandoRef.current = false;
    }
  };

  // FP5: salvar um TRECHO — soma páginas, guarda a faixa, NÃO move o cursor linear
  const handleSaveSegment = async () => {
    if (!onSaveSegment || segPages <= 0 || segSaving) return;
    setSegSaving(true); setError(null);
    try {
      await onSaveSegment(book.id, segFrom, segTo, focusMinutes, segLabel.trim() || null);
      setFocusMinutes(null); setFocusSeconds(null);
      setCelebrating(true);
      setTimeout(() => { setCelebrating(false); onClose(); }, 1200);
    } catch (e: any) {
      setError(e.message || 'Erro ao salvar trecho');
    } finally {
      setSegSaving(false);
    }
  };

  const segBtn: React.CSSProperties = { width: 36, height: 40, borderRadius: 10, background: 'var(--paper)', color: 'var(--ink)', fontSize: 18, fontWeight: 500, border: '1px solid var(--line)', cursor: 'pointer', lineHeight: 1, flex: 'none' };
  // O número PREENCHE o espaço livre (flex:1) em vez de largura fixa — nunca corta,
  // nem com 3-4 dígitos. appearance:none remove o spinner do input number no iOS.
  const segInput: React.CSSProperties = { flex: 1, minWidth: 0, width: '100%', fontFamily: "Geist, Geist Fallback, -apple-system, system-ui, sans-serif", fontSize: 30, fontWeight: 600, lineHeight: 1, color: 'var(--ember)', background: 'transparent', border: 'none', textAlign: 'center', outline: 'none', padding: 0, boxSizing: 'border-box', WebkitAppearance: 'none', MozAppearance: 'textfield' };

  return (
    <Sheet open={open && !pausado} onClose={onClose}>
      {celebrating ? (
        <Celebration delta={mode === 'trecho' ? segPages : delta} pct={pct} finished={mode === 'trecho' ? false : finished} unitLong={(mode === 'trecho' ? segPages : delta) === 1 ? u.one : u.long}/>
      ) : conviteTrilha > 0 ? (
        <ConviteTrilha n={conviteTrilha} travessia={travessia}
          onLer={() => { onOpenTrilha && onOpenTrilha(book.id); onClose(); }}
          onDepois={() => { onDispensarTrilha && onDispensarTrilha(); onClose(); }} />
      ) : annotateInvite ? (
        <div style={{ padding: '8px 4px 6px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, margin: '6px auto 16px', borderRadius: '50%', background: 'var(--ember-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="pen" size={24} color="var(--ember)"/>
          </div>
          <div className="serif" style={{ fontSize: 23, lineHeight: 1.15, color: 'var(--ink)', maxWidth: 300, margin: '0 auto 8px' }}>Anote sobre o que você leu</div>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-3)', maxWidth: 290, margin: '0 auto 22px' }}>Uma frase basta pra consolidar o conhecimento — um grifo, uma ideia, uma pergunta.</div>
          <button onClick={() => { onOpenStudy && onOpenStudy(book.id, annotatePage); onClose(); }} style={{ width: '100%', padding: 15, borderRadius: 14, border: 'none', background: 'var(--ember)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Anotar no caderno</button>
          <button onClick={onClose} style={{ width: '100%', marginTop: 10, padding: 12, borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 14, cursor: 'pointer' }}>Agora não</button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{syncMode ? 'Corrigir página' : pedirInicio ? 'Antes de registrar' : 'Registrar'}</div>
              <div className="serif" style={{ fontSize: 26 }}>{syncMode || pedirInicio ? `Em que ${u.where} você está?` : 'Até onde você leu?'}</div>
            </div>
            <button onClick={() => syncMode ? setSyncMode(false) : onClose()} style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--paper-2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><Icon name={syncMode ? 'chevron-left' : 'close'} size={16}/></button>
          </div>

          {/* Os tres modos VISIVEIS, lado a lado. Antes o Modo foco morava
              dentro de um menu suspenso: para usar o cronometro a pessoa
              precisava abrir o seletor, ler tres linhas e escolher — dois
              toques e uma decisao para chegar no que e, na pratica, o jeito
              mais bonito de usar o app. Escondido, ele simplesmente nao era
              usado. Agora custa um toque, como "De onde parei". */}
          {!syncMode && !(mode === 'foco' && focusMinutes === null) && (() => {
            const META: Record<'linear' | 'foco' | 'trecho', { ic: string; label: string; curto: string; sub: string }> = {
              linear: { ic: 'book', label: 'De onde parei', curto: 'De onde parei', sub: 'conta do seu ponto até agora' },
              foco: { ic: 'timer', label: 'Modo foco', curto: 'Modo foco', sub: 'cronômetro + registra' },
              trecho: { ic: 'bookmark', label: 'Li um trecho', curto: 'Um trecho', sub: 'só o pedaço que leu' },
            };
            const pick = (m: 'linear' | 'foco' | 'trecho') => {
              if (m === 'foco') { setFocusMinutes(null); setFocusSeconds(null); }
              setMode(m);
            };
            return (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['linear', 'foco', 'trecho'] as const).map((m) => {
                    const on = m === mode;
                    return (
                      <button key={m} onClick={() => pick(m)} style={{
                        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 5,
                        padding: '10px 6px', borderRadius: 12, cursor: 'pointer',
                        border: `1px solid ${on ? 'var(--ember)' : 'var(--line)'}`,
                        background: on ? 'var(--ember-soft)' : 'var(--paper-2)',
                        transition: 'background .15s, border-color .15s',
                      }}>
                        <Icon name={META[m].ic} size={16} color={on ? 'var(--ember)' : 'var(--ink-3)'} />
                        <span style={{
                          fontSize: 11.5, fontWeight: on ? 700 : 500, lineHeight: 1.15,
                          color: on ? 'var(--ember-2)' : 'var(--ink-2)',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
                        }}>{META[m].curto}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', textAlign: 'center', marginTop: 7 }}>
                  {META[mode].sub}
                </div>
              </div>
            );
          })()}

          {mode === 'foco' && focusMinutes === null ? (
            <FocusTimer
              bookTitle={book.title}
              bookId={book.id}
              encerrarAoRestaurar={registrarSessaoSalva}
              rotuloSaida="Só registrar a página"
              // AO ENCERRAR, CAI EM "DE ONDE PAREI" — sem tela de escolha.
              //
              // Antes o cronômetro entregava uma pergunta ("Esse tempo foi
              // de…") com duas cartas, e a de "Um trecho" é que vinha vestida
              // de principal, em ember. Quem acabou de ler tinha que decidir de
              // novo, e o destaque apontava para o caso raro: trecho é a
              // exceção, ler de onde parou é o que acontece quase sempre.
              // Os três modos continuam na fileira acima — "Um trecho" segue a
              // um toque, com o tempo da sessão preservado.
              onFinish={(min, sec) => { setFocusMinutes(min); setFocusSeconds(sec); setMode('linear'); }}
              onCancel={() => { setMode('linear'); }}
            />
          ) : (
          <>
          {/* Resultado da sessão de foco — atualiza conforme define a página */}
          {focusMinutes != null && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--ember-soft)', border: '1px solid var(--ember)', borderRadius: 14, padding: '11px 13px', margin: '2px 0 4px' }}>
              <Icon name="clock" size={15} color="var(--ember-2)"/>
              <div style={{ flex: 1, fontSize: 12.5, color: 'var(--ink)', lineHeight: 1.5 }}>
                Sessão de foco: <strong>{focusMinutes} min</strong>
                {focusSpeed && <> · <strong>{focusSpeed.toFixed(1)} {u.short}/min</strong></>}
                {toFinishMin != null && !finished && <div style={{ color: 'var(--ink-3)', marginTop: 2 }}>Nesse ritmo, ~{toFinishMin} min pra terminar{perDayMin ? ` · ~${perDayMin} min/dia pra meta` : ''}</div>}
              </div>
              <button onClick={() => setFocusMinutes(null)} style={{ background: 'none', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 0', marginTop: 4 }}>
            <BookCover book={book} width={44} height={66}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="serif" style={{ fontSize: 16, lineHeight: 1.1 }}>{book.title}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{syncMode ? `hoje no app: ${ref(book.read)}` : mode === 'trecho' ? 'registrar um trecho lido' : pedirInicio ? 'primeiro registro deste livro' : `você está na ${ref(book.read)}`}</div>
            </div>
          </div>

          {syncMode ? (
            <>
              {/* Explicacao curta do que faz */}
              <div style={{
                padding: '12px 14px', borderRadius: 14,
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.45,
                marginTop: 6,
              }}>
                Diga em que {u.where} você está de verdade. As próximas leituras
                passam a contar a partir dela —
                <strong style={{ color: 'var(--ink-2)' }}> a correção não soma como leitura</strong>. Seu histórico é preservado.
              </div>

              {/* Input de pagina atual — visual igual ao do registro normal */}
              <div style={{
                background: 'var(--paper-2)', borderRadius: 22,
                padding: '28px 20px', marginTop: 12,
                border: '1px solid var(--line)', textAlign: 'center',
              }}>
                <div className="mono" style={{
                  fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: 'var(--ink-3)', marginBottom: 12,
                }}>Estou agora em</div>
                <input
                  type="number"
                  min={0} max={book.pages}
                  value={syncInput}
                  onChange={e => setSyncInput(e.target.value)}
                  style={{
                    width: 180, fontFamily: 'Instrument Serif, serif',
                    fontSize: 72, lineHeight: 1, letterSpacing: '-0.03em',
                    color: 'var(--ember)', background: 'transparent',
                    border: 'none', textAlign: 'center', outline: 'none',
                  }}
                />
                <div className="mono" style={{
                  fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: 'var(--ink-3)', marginTop: 4,
                }}>de {book.pages} {u.short}</div>
              </div>

              {syncError && (
                <div style={{
                  marginTop: 12, padding: '10px 14px', borderRadius: 12,
                  background: 'rgba(232,122,59,0.12)', color: 'var(--ember)',
                  fontSize: 13, textAlign: 'center',
                }}>{syncError}</div>
              )}

              <button onClick={doSync} disabled={syncSaving} style={{
                width: '100%', marginTop: 18,
                background: syncSaving ? 'var(--paper-3)' : 'var(--ink)',
                color: syncSaving ? 'var(--ink-4)' : 'var(--paper)',
                padding: '16px', borderRadius: 16, fontSize: 15, fontWeight: 500,
              }}>
                {syncSaving ? 'Salvando...' : 'Salvar minha página'}
              </button>
              <button onClick={() => setSyncMode(false)} style={{
                width: '100%', marginTop: 8,
                background: 'transparent', color: 'var(--ink-3)',
                padding: '12px', borderRadius: 12, fontSize: 13,
              }}>Cancelar</button>
            </>
          ) : mode === 'trecho' ? (
            <div style={{ background: 'var(--paper-2)', borderRadius: 22, padding: '22px 18px', marginTop: 8, border: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 8, textAlign: 'center' }}>Da página</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
                    <button onClick={() => setSegFrom(v => Math.max(1, v - 1))} style={segBtn}>−</button>
                    <input type="number" min={1} max={book.pages} value={segFrom}
                      onChange={e => { const n = parseInt(e.target.value, 10); if (Number.isFinite(n)) { const v = Math.max(1, Math.min(book.pages, n)); setSegFrom(v); if (v > segTo) setSegTo(v); } }}
                      style={segInput}/>
                    <button onClick={() => setSegFrom(v => { const nv = Math.min(book.pages, v + 1); if (nv > segTo) setSegTo(nv); return nv; })} style={segBtn}>+</button>
                  </div>
                </div>
                <div style={{ paddingBottom: 12, color: 'var(--ink-3)' }}><Icon name="arrow-right" size={16}/></div>
                <div style={{ flex: 1 }}>
                  <div className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 8, textAlign: 'center' }}>até a</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
                    <button onClick={() => setSegTo(v => Math.max(segFrom, v - 1))} style={segBtn}>−</button>
                    <input type="number" min={segFrom} max={book.pages} value={segTo}
                      onChange={e => { const n = parseInt(e.target.value, 10); if (Number.isFinite(n)) setSegTo(Math.max(1, Math.min(book.pages, n))); }}
                      style={segInput}/>
                    <button onClick={() => setSegTo(v => Math.min(book.pages, v + 1))} style={segBtn}>+</button>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13, fontWeight: 600, color: segPages > 0 ? 'var(--ember)' : 'var(--ink-3)' }}>
                {segPages > 0 ? `${segPages} ${segPages === 1 ? u.short : u.long} neste trecho` : 'A página final deve ser ≥ a inicial'}
              </div>
              <input value={segLabel} onChange={e => setSegLabel(e.target.value)} maxLength={120}
                placeholder="Nome do trecho — opcional (ex.: história do mercador)"
                style={{ width: '100%', marginTop: 14, padding: '11px 13px', borderRadius: 12, border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}/>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.45, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <span style={{ marginTop: 1 }}><Icon name="bookmark" size={13} color="var(--ink-3)"/></span>
                <span>Soma ao que você já leu e <strong style={{ color: 'var(--ink-2)' }}>não move o "de onde parei"</strong> (segue na pág. {book.read}).</span>
              </div>
            </div>
          ) : pedirInicio ? (
            <div data-pagina-inicio>
              <div style={{
                background: 'var(--paper-2)', borderRadius: 22,
                padding: '24px 20px', marginTop: 8,
                border: '1px solid var(--line)', textAlign: 'center',
              }}>
                <div className="mono" style={{
                  fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: 'var(--ink-3)', marginBottom: 10,
                }}>Estou na {u.where}</div>
                <input
                  type="number" inputMode="numeric"
                  min={0} max={book.pages}
                  value={inicioInput}
                  placeholder="0"
                  aria-label={`Em que ${u.where} você está`}
                  onChange={e => { setInicioInput(e.target.value); setInicioErro(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') definirInicio(); }}
                  style={{
                    width: 180, fontFamily: 'Instrument Serif, serif',
                    fontSize: 72, lineHeight: 1, letterSpacing: '-0.03em',
                    color: 'var(--ember)', background: 'transparent',
                    border: 'none', textAlign: 'center', outline: 'none',
                  }}
                />
                <div className="mono" style={{
                  fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: 'var(--ink-3)', marginTop: 4,
                }}>de {book.pages} {u.short}</div>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.45, marginTop: 12, textAlign: 'center', padding: '0 6px' }}>
                Antes da leitura de hoje, em que {u.where} você estava? Ela vira o ponto
                de partida e <strong style={{ color: 'var(--ink-2)' }}>não conta como {u.long} lidas</strong>.
              </div>
              {inicioErro && (
                <div style={{
                  marginTop: 12, padding: '10px 14px', borderRadius: 12,
                  background: 'rgba(232,122,59,0.12)', color: 'var(--ember)',
                  fontSize: 13, textAlign: 'center',
                }}>{inicioErro}</div>
              )}
              <button onClick={() => definirInicio()} disabled={inicioSalvando || inicioInput.trim() === ''} style={{
                width: '100%', marginTop: 16,
                background: !inicioSalvando && inicioInput.trim() !== '' ? 'var(--ink)' : 'var(--paper-3)',
                color: !inicioSalvando && inicioInput.trim() !== '' ? 'var(--paper)' : 'var(--ink-4)',
                padding: '16px', borderRadius: 16, fontSize: 15, fontWeight: 500,
              }}>
                {inicioSalvando ? 'Salvando...' : inicioInput.trim() === '' ? `Digite a ${u.where} em que está` : `Começar da ${ref(parseInt(inicioInput, 10) || 0)}`}
              </button>
              <button onClick={() => definirInicio(0)} disabled={inicioSalvando} style={{
                width: '100%', marginTop: 8,
                background: 'transparent', color: 'var(--ink-2)',
                padding: '12px', borderRadius: 12, fontSize: 13.5,
                border: '1px solid var(--line)',
              }}>Estou começando o livro agora</button>
            </div>
          ) : (
          <div style={{
            background: 'var(--paper-2)', borderRadius: 22,
            padding: '28px 20px', marginTop: 8,
            border: '1px solid var(--line)', textAlign: 'center',
          }}>
            <div className="mono" data-da-ate style={{
              fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginBottom: 6,
            }}>{daAte}</div>
            <NumeroPagina page={page} max={book.pages} rotulo={`Até que ${u.where} você leu`} onChange={setPage}/>
            <div className="mono" style={{
              fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginTop: 4,
            }}>de {book.pages} {u.short}</div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 18 }}>
              {(u.step >= 10 ? [-100, -10, +10, +100] : [-10, -1, +1, +10]).map(n => (
                <button key={n} onClick={() => inc(n)} style={{
                  width: 54, height: 44, borderRadius: 12,
                  background: 'var(--paper)', color: 'var(--ink)',
                  fontSize: 15, fontWeight: 500, border: '1px solid var(--line)',
                }}>{n > 0 ? `+${n}` : n}</button>
              ))}
            </div>

            <ReguaPagina page={page} max={book.pages} step={u.step} rotulo={`Arraste até a ${u.where} em que parou`} onChange={setPage}/>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--ink-4)', fontFamily: 'Geist Mono, monospace' }}>
              <span>0</span>
              <span>{book.pages}</span>
            </div>
          </div>
          )}

          {!syncMode && mode !== 'trecho' && !pedirInicio && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            <div style={{
              padding: '6px 12px', borderRadius: 999,
              background: delta > 0 ? 'var(--ember-soft)' : 'var(--paper-2)',
              color: delta > 0 ? 'var(--ember)' : 'var(--ink-3)',
              fontSize: 13, fontWeight: 500,
            }}>
              {delta > 0 ? `+${delta} ${delta === 1 ? u.one : u.long} nesta sessão` : `Mova até a ${u.where} em que você parou`}
            </div>
          </div>
          )}

          {/* "Na verdade estou na p. Y": à vista, na própria folha. Antes era
              uma pílula que só aparecia depois do primeiro registro — ou seja,
              sumia justamente quando mais fazia falta. */}
          {!syncMode && mode === 'linear' && !pedirInicio && onSyncPage && (
            <button
              data-corrigir-pagina
              onClick={() => { setSyncInput(String(book.read || 0)); setSyncMode(true); }}
              style={{
                display: 'block', margin: '10px auto 0', padding: '4px 8px',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--ember)', fontSize: 12.5,
                textDecoration: 'underline', textUnderlineOffset: 3,
              }}
            >
              Não está na {ref(book.read)}? Corrigir sem contar como leitura
            </button>
          )}

          {!syncMode && !pedirInicio && error && (
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: 12,
              background: 'rgba(232,122,59,0.12)', color: 'var(--ember)',
              fontSize: 13, textAlign: 'center',
            }}>{error}</div>
          )}

          {!syncMode && mode === 'trecho' && (
          <button onClick={handleSaveSegment} disabled={segPages <= 0 || segSaving} style={{
            width: '100%', marginTop: 18,
            background: segPages > 0 && !segSaving ? 'var(--ink)' : 'var(--paper-3)',
            color: segPages > 0 && !segSaving ? 'var(--paper)' : 'var(--ink-4)',
            padding: '16px', borderRadius: 16, fontSize: 15, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <Icon name="bookmark" size={15} color={segPages > 0 && !segSaving ? 'var(--paper)' : 'var(--ink-4)'}/>
            {segSaving ? 'Salvando...' : 'Guardar trecho'}
          </button>
          )}
          {!syncMode && mode !== 'trecho' && !pedirInicio && (
          <button onClick={handleSave} disabled={delta <= 0 || saving} style={{
            width: '100%', marginTop: 18,
            background: delta > 0 && !saving ? 'var(--ink)' : 'var(--paper-3)',
            color: delta > 0 && !saving ? 'var(--paper)' : 'var(--ink-4)',
            padding: '16px', borderRadius: 16, fontSize: 15, fontWeight: 500,
          }}>
            {saving ? 'Salvando...' : delta <= 0 ? `Marque a ${u.where} em que parou` : finished ? 'Terminar livro' : 'Salvar sessão'}
          </button>
          )}

          {/* Atalho discreto pra editar registros antigos. Sem isso, user não
             descobre intuitivamente a rota Perfil → Minhas leituras. */}
          {!syncMode && onOpenReadings && (
            <button
              onClick={() => { onClose(); onOpenReadings(); }}
              style={{
                width: '100%', marginTop: 10,
                background: 'transparent', color: 'var(--ink-3)',
                padding: '10px', borderRadius: 12, fontSize: 12.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <Icon name="edit" size={13} color="var(--ink-3)"/>
              Editar registros anteriores
            </button>
          )}
          </>
          )}
        </>
      )}
    </Sheet>
  );
};

function Celebration({ delta, pct, finished, unitLong = 'páginas' }: { delta: number; pct: number; finished: boolean; unitLong?: string }) {
  return (
    <div data-comemoracao="folha" style={{ padding: '30px 0 10px', textAlign: 'center' }}>
      <div style={{
        width: 120, height: 120, borderRadius: '50%',
        background: 'var(--ember-soft)', margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
      }}>
        <Flame count={20} size={72}/>
      </div>
      <div className="serif" style={{ fontSize: 34, marginTop: 20, lineHeight: 1.05 }}>
        {finished ? 'Livro terminado!' : `+${delta} ${unitLong}`}
      </div>
      <div style={{ fontStyle: 'italic', fontSize: 16, color: 'var(--ink-3)', marginTop: 6 }}>
        {finished ? 'Um capítulo fecha, outro começa.' : 'A chama segue acesa.'}
      </div>
      <div style={{ marginTop: 22, display: 'flex', justifyContent: 'center', gap: 6 }}>
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} style={{
            width: 6, height: i < Math.ceil(pct / 10) ? 22 : 6, borderRadius: 3,
            background: 'var(--ember)', opacity: 0.4 + (i / 10) * 0.6,
            transition: `height 300ms ${i * 40}ms`,
          }}/>
        ))}
      </div>
      <div className="mono" style={{
        fontSize: 11, marginTop: 20, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)',
      }}>{pct}% completo</div>
    </div>
  );
}
